import { useState } from 'react';
import { AlertCircle, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { MetaIntegrationModal } from '@/components/settings/integrations/meta-integration-modal';
import { useGoogleAdsConnection } from '@/hooks/useGoogleAdsConnection';
import { useMetaIntegrationStatus } from '@/lib/query-options';

interface PlatformConnectionBannerProps {
  platform: 'google' | 'meta';
  /**
   * The platform token is known-revoked — "reconnect" wording instead of "connect".
   * Google callers pass it from /user/me (#5935); for Meta the banner derives it itself
   * from the integration status it already fetches (#5949), so callers may omit it.
   */
  broken?: boolean;
}

const COPY = {
  google: {
    connect: {
      title: 'Google Ads Is Not Connected',
      body: 'Google Ads campaigns cannot be created, edited or launched until Google Ads is connected. Existing campaigns remain viewable.',
      cta: 'Connect Google Ads',
    },
    reconnect: {
      title: 'Google Ads Connection Lost',
      body: 'The Google Ads connection has expired or was revoked. Google Ads campaigns cannot be created, edited or launched until it is reconnected.',
      cta: 'Reconnect Google Ads',
    },
  },
  meta: {
    connect: {
      title: 'Meta Is Not Connected',
      body: 'Meta campaigns cannot be created, edited or launched until Meta is connected and an ad account is selected. Existing campaigns remain viewable.',
      cta: 'Connect Meta',
    },
    reconnect: {
      title: 'Meta Connection Lost',
      body: 'The Meta connection has expired or was revoked. Meta campaigns cannot be created, edited or launched until it is reconnected.',
      cta: 'Reconnect Meta',
    },
  },
} as const;

/**
 * Per-platform "not connected" banner for campaign surfaces (#5937/#5907).
 *
 * Rendered where a platform's actions are disabled in the middle of a task: the Google
 * campaign detail page, the Meta campaign detail header and the creation wizard
 * pickers — styled to match the amber read-only notice family.
 *
 * The CTA connects IN PLACE (design review on #5958): the user always ends up back where
 * they were, never on a different page. Google's OAuth runs in a popup straight from the
 * button (#6668, there is no Google connect modal) — on success the connection signals
 * refresh and the banner disappears under the user. Meta's OAuth is a full-page redirect by
 * nature, but its modal still spares the Settings detour. Deliberately says nothing about
 * the other platform: each banner describes only its own platform, so the copy holds
 * whether the org has one platform or both.
 */
export function PlatformConnectionBanner({ platform, broken = false }: PlatformConnectionBannerProps) {
  const [isConnectModalOpen, setIsConnectModalOpen] = useState(false);
  // Only issued for the Meta banner — the modal needs the status object (scopes, account).
  const { data: metaStatus } = useMetaIntegrationStatus({ enabled: platform === 'meta' });
  const googleAdsConnection = useGoogleAdsConnection();

  const isBroken = broken || (platform === 'meta' && metaStatus?.connection_broken === true);
  const copy = isBroken ? COPY[platform].reconnect : COPY[platform].connect;
  const isConnecting = platform === 'google' && googleAdsConnection.isAuthorizing;

  return (
    <div className="bg-amber-50 border-l-4 border-l-amber-400 rounded-lg p-4 mb-4">
      <div className="flex items-start gap-4">
        <AlertCircle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
        <div className="flex-1 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-gray-900">{copy.title}</p>
            <p className="text-sm text-gray-700 mt-0.5">{copy.body}</p>
          </div>
          <Button
            variant="outline"
            onClick={() => (platform === 'google' ? googleAdsConnection.connect() : setIsConnectModalOpen(true))}
            disabled={isConnecting}
            className="h-9 text-gray-900 shrink-0"
          >
            {isConnecting ? 'Connecting...' : copy.cta}
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>
      {platform === 'meta' && (
        <MetaIntegrationModal
          isOpen={isConnectModalOpen}
          onClose={() => setIsConnectModalOpen(false)}
          integrationStatus={metaStatus || null}
        />
      )}
    </div>
  );
}
